import { generateRandomKey } from 'utils';
import pica from 'pica';
import heic2any from 'heic2any';

export function dataURItoFile(dataURI: string) {
  // Split the dataURI string to get the base64 data
  let arr = dataURI.split(',');

  // Extract the Base64 content
  let mime = arr?.[0].match(/:(.*?);/)?.[1];
  let bstr = atob(arr[1]);

  // Convert base64 to binary format
  let n = bstr.length;
  let u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], generateRandomKey(), { type: mime });
}

async function dataURLToBlob(dataUrl: string) {
  const res = await fetch(dataUrl);
  return await res.blob();
}

function loadImage(blob) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

function getFileName(input, ext) {
  if (typeof input === 'string') return `image.${ext}`;
  const base = input.name?.replace(/\.[^.]+$/, '') || 'image';
  return `${base}.${ext}`;
}

export async function resizeV2(
  input: Blob | File | string,
  resize = { width: 1600, height: 1200 },
  newFormat = 'jpeg',
  quality = 0.8
) {
  let blob = typeof input === 'string' ? await dataURLToBlob(input) : input;
  if (blob.type === 'image/heic' || (blob instanceof File && blob.name?.endsWith('.heic'))) {
    blob = (await heic2any({ blob, toType: 'image/jpeg' })) as Blob;
  }

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        let { width: imgWidth, height: imgHeight } = img;
        let targetWidth = resize?.width || imgWidth;
        let targetHeight = resize?.height || imgHeight;

        let drawWidth = imgWidth;
        let drawHeight = imgHeight;
        let offsetX = 0;
        let offsetY = 0;

        // Fill the canvas, crop overflow (Sharp default)
        const scale = Math.max(targetWidth / imgWidth, targetHeight / imgHeight);
        drawWidth = imgWidth * scale;
        drawHeight = imgHeight * scale;

        // Center crop by default
        offsetX = (targetWidth - drawWidth) / 2;
        offsetY = (targetHeight - drawHeight) / 2;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Convert to blob
        const mimeType = `image/${newFormat === 'jpg' ? 'jpeg' : newFormat}`;

        canvas.toBlob(
          newBlob => {
            if (!newBlob) {
              reject(new Error('Failed to create blob'));
              return;
            }

            resolve(newBlob);
          },
          mimeType,
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(blob);
  });
}
